const ArgumentType = require('../../extension-support/argument-type');
const BlockType = require('../../extension-support/block-type');
const ElioSerial = require('./ElioSerial');
const ElioBle = require('./ElioBle');
const {makeOutputBuffer, IDX, CMD_SENSOR_CFG, UDP} = require('./ElioPacket');

const ELIO_ID = 'elio';

// 블록 텍스트 (한국어)
const M = {
    connect: 'ELIO [LINK] 로 연결하기',
    disconnect: 'ELIO 연결 해제',
    setDC: '[PORT] 모터를 [POWER] % 로 설정',
    setDCDual: 'DC1 [P1] % DC2 [P2] % 동시 설정',
    setSV: '[PORT] 서보를 [ANGLE] 도로 설정',
    setSVDual: 'SV1 [A1] 도 SV2 [A2] 도 동시 설정',
    setIO: '[PORT] 출력을 [VALUE] 으로 설정',
    stopAll: '모두 정지',
    sensorCfg: '[SENSOR] 센서 [ONOFF]',
    getSonic: '초음파 거리 (cm)',
    getLine1: '라인1 감지값',
    getLine2: '라인2 감지값',
    getTemp: '온도',
    getHumid: '습도',
    getWater: '수분'
};

const SENSOR_BIT = {SONIC: 0x01, LINE1: 0x02, LINE2: 0x04};

class Scratch3ElioExtension {
    constructor (runtime) {
        this._runtime = runtime;
        // 연결 통로 — 같은 모양(connect/disconnect/send/status/env). 블록은 통로를 모른다.
        this._links = {USB: new ElioSerial(), BLE: new ElioBle()};
        this._elio = this._links.USB;
        this._outBuf = makeOutputBuffer();
        this._sensorFlags = 0;

        Object.values(this._links).forEach(link => {
            link.onConnect = () => {
                this._runtime.emit('PERIPHERAL_CONNECTED', {name: 'ELIO', path: ''});
            };
            link.onDisconnect = () => {
                this._runtime.emit('PERIPHERAL_DISCONNECTED', {name: 'ELIO'});
            };
        });

        // 메뉴바에서 접근할 수 있도록 runtime에 참조 저장
        this._runtime._elioExtension = this;

        // 스크래치 정지 버튼 → 전체 출력 0
        this._runtime.on('PROJECT_STOP_ALL', () => this.stopAll());
    }

    getInfo () {
        return {
            id: ELIO_ID,
            name: 'ELIO',
            color1: '#1E5FA8',
            color2: '#174A82',
            color3: '#0F3566',
            menuIconURI: null,
            blockIconURI: null,

            blocks: [
                // ── 연결 ─────────────────────────────────
                {
                    opcode: 'connectElio',
                    blockType: BlockType.COMMAND,
                    text: M.connect,
                    arguments: {
                        LINK: {type: ArgumentType.STRING, menu: 'links', defaultValue: 'BLE'}
                    }
                },
                {
                    opcode: 'disconnectElio',
                    blockType: BlockType.COMMAND,
                    text: M.disconnect
                },
                '---',

                // ── DC 모터 ───────────────────────────────
                {
                    opcode: 'setDC',
                    blockType: BlockType.COMMAND,
                    text: M.setDC,
                    arguments: {
                        PORT: {type: ArgumentType.STRING, menu: 'dcPorts', defaultValue: 'DC1'},
                        POWER: {type: ArgumentType.NUMBER, defaultValue: 50}
                    }
                },
                {
                    opcode: 'setDCDual',
                    blockType: BlockType.COMMAND,
                    text: M.setDCDual,
                    arguments: {
                        P1: {type: ArgumentType.NUMBER, defaultValue: 50},
                        P2: {type: ArgumentType.NUMBER, defaultValue: 50}
                    }
                },
                '---',

                // ── 서보 모터 ─────────────────────────────
                {
                    opcode: 'setSV',
                    blockType: BlockType.COMMAND,
                    text: M.setSV,
                    arguments: {
                        PORT: {type: ArgumentType.STRING, menu: 'svPorts', defaultValue: 'SV1'},
                        ANGLE: {type: ArgumentType.NUMBER, defaultValue: 0}
                    }
                },
                {
                    opcode: 'setSVDual',
                    blockType: BlockType.COMMAND,
                    text: M.setSVDual,
                    arguments: {
                        A1: {type: ArgumentType.NUMBER, defaultValue: 0},
                        A2: {type: ArgumentType.NUMBER, defaultValue: 0}
                    }
                },
                '---',

                // ── IO 출력 ───────────────────────────────
                {
                    opcode: 'setIO',
                    blockType: BlockType.COMMAND,
                    text: M.setIO,
                    arguments: {
                        PORT: {type: ArgumentType.STRING, menu: 'ioPorts', defaultValue: 'IO1'},
                        VALUE: {type: ArgumentType.NUMBER, defaultValue: 100}
                    }
                },
                {
                    opcode: 'stopAll',
                    blockType: BlockType.COMMAND,
                    text: M.stopAll
                },
                '---',

                // ── 센서 설정 ─────────────────────────────
                {
                    opcode: 'sensorCfg',
                    blockType: BlockType.COMMAND,
                    text: M.sensorCfg,
                    arguments: {
                        SENSOR: {type: ArgumentType.STRING, menu: 'sensorTypes', defaultValue: 'SONIC'},
                        ONOFF: {type: ArgumentType.STRING, menu: 'onOff', defaultValue: 'ON'}
                    }
                },
                '---',

                // ── 센서 리포터 ───────────────────────────
                {
                    opcode: 'getSonic',
                    blockType: BlockType.REPORTER,
                    text: M.getSonic
                },
                {
                    opcode: 'getLine1',
                    blockType: BlockType.REPORTER,
                    text: M.getLine1
                },
                {
                    opcode: 'getLine2',
                    blockType: BlockType.REPORTER,
                    text: M.getLine2
                },
                {
                    opcode: 'getTemperature',
                    blockType: BlockType.REPORTER,
                    text: M.getTemp
                },
                {
                    opcode: 'getHumidity',
                    blockType: BlockType.REPORTER,
                    text: M.getHumid
                },
                {
                    opcode: 'getWater',
                    blockType: BlockType.REPORTER,
                    text: M.getWater
                }
            ],

            menus: {
                links: {acceptReporters: false, items: [{text: '블루투스', value: 'BLE'}, {text: 'USB 동글', value: 'USB'}]},
                dcPorts: {acceptReporters: false, items: ['DC1', 'DC2']},
                svPorts: {acceptReporters: false, items: ['SV1', 'SV2']},
                ioPorts: {acceptReporters: false, items: ['IO1', 'IO2', 'IO3', 'IO4', 'V3', 'V5']},
                sensorTypes: {acceptReporters: false, items: ['SONIC', 'LINE1', 'LINE2']},
                onOff: {acceptReporters: false, items: ['ON', 'OFF']}
            }
        };
    }

    // ── 블록 핸들러 ───────────────────────────────────────

    /**
     * @param {object} args 블록 인자
     * @param {string} [args.LINK] 'BLE' | 'USB' — 옛 프로젝트(인자 없음)는 USB
     */
    async connectElio ({LINK} = {}) {
        if (this._elio.connected) await this._elio.disconnect();
        this._elio = this._links[LINK] || this._links.USB;
        await this._elio.connect();
        if (this._sensorFlags) await this._elio.send(new Uint8Array([UDP, CMD_SENSOR_CFG, this._sensorFlags]));
        await this._flush();
    }

    /** @returns {?string} 연결된 통로 'BLE' | 'USB', 없으면 null */
    get link () {
        return this._elio.connected ? Object.keys(this._links).find(k => this._links[k] === this._elio) : null;
    }

    async disconnectElio () {
        await this.stopAll();
        await this._elio.disconnect();
    }

    async setDC ({PORT, POWER}) {
        this._outBuf[PORT === 'DC2' ? IDX.DC2 : IDX.DC1] = Math.max(-100, Math.min(100, Math.round(Number(POWER))));
        await this._flush();
    }

    async setDCDual ({P1, P2}) {
        this._outBuf[IDX.DC1] = Math.max(-100, Math.min(100, Math.round(Number(P1))));
        this._outBuf[IDX.DC2] = Math.max(-100, Math.min(100, Math.round(Number(P2))));
        await this._flush();
    }

    async setSV ({PORT, ANGLE}) {
        this._outBuf[PORT === 'SV2' ? IDX.SV2 : IDX.SV1] = Math.max(-90, Math.min(90, Math.round(Number(ANGLE))));
        await this._flush();
    }

    async setSVDual ({A1, A2}) {
        this._outBuf[IDX.SV1] = Math.max(-90, Math.min(90, Math.round(Number(A1))));
        this._outBuf[IDX.SV2] = Math.max(-90, Math.min(90, Math.round(Number(A2))));
        await this._flush();
    }

    async setIO ({PORT, VALUE}) {
        const map = {IO1: IDX.IO1, IO2: IDX.IO2, IO3: IDX.IO3, IO4: IDX.IO4, V3: IDX.V3, V5: IDX.V5};
        const idx = map[PORT];
        if (typeof idx === 'number') {
            this._outBuf[idx] = Math.max(0, Math.min(100, Math.round(Number(VALUE))));
            await this._flush();
        }
    }

    async stopAll () {
        this._outBuf = makeOutputBuffer();
        await this._flush();
    }

    async sensorCfg ({SENSOR, ONOFF}) {
        const bit = SENSOR_BIT[SENSOR];
        if (!bit) return;
        if (ONOFF === 'ON') this._sensorFlags |= bit;
        else this._sensorFlags &= ~bit;
        await this._elio.send(new Uint8Array([UDP, CMD_SENSOR_CFG, this._sensorFlags]));
    }

    getSonic () {
        return this._elio.status.SONIC;
    }
    getLine1 () {
        return this._elio.status.LINE1;
    }
    getLine2 () {
        return this._elio.status.LINE2;
    }
    getTemperature () {
        return this._elio.env.TEMPERATURE;
    }
    getHumidity () {
        return this._elio.env.HUMIDITY;
    }
    getWater () {
        return this._elio.env.WATER;
    }

    async _flush () {
        if (this._elio.connected) await this._elio.send(this._outBuf);
    }
}

module.exports = Scratch3ElioExtension;
